import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RotateCcw, Trash2, Loader2, Check, ImagePlus, Film } from 'lucide-react';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store';
import { useTranslation } from '@/features/i18n/locales';
import { createLogger } from '@/lib/logger';

const log = createLogger('SettingsDialog');

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Clear regenerable cache data for the current project's media only.
 */
async function clearProjectCaches(mediaIds: string[]): Promise<void> {
  if (mediaIds.length === 0) return;

  const [
    { deleteWaveform },
    { deleteGifFrames },
    { deleteDecodedPreviewAudio },
    { gifFrameCache },
    { filmstripCache },
    { waveformCache },
  ] = await Promise.all([
    import('@/lib/storage/indexeddb/waveforms'),
    import('@/lib/storage/indexeddb/gif-frames'),
    import('@/lib/storage/indexeddb/decoded-preview-audio'),
    import('@/features/timeline/services/gif-frame-cache'),
    import('@/features/timeline/services/filmstrip-cache'),
    import('@/features/timeline/services/waveform-cache'),
  ]);

  const { clearPreviewAudioCache } = await import(
    '@/lib/composition-runtime/utils/audio-decode-cache'
  );
  clearPreviewAudioCache();

  await Promise.all(
    mediaIds.flatMap((id) => [
      deleteWaveform(id).catch(() => { }),
      deleteGifFrames(id).catch(() => { }),
      deleteDecodedPreviewAudio(id).catch(() => { }),
      gifFrameCache.clearMedia(id).catch(() => { }),
      filmstripCache.clearMedia(id).catch(() => { }),
      waveformCache.clearMedia(id).catch(() => { }),
    ])
  );
}

/** Delete all proxy videos for the given media IDs */
async function clearProjectProxies(mediaIds: string[]): Promise<void> {
  if (mediaIds.length === 0) return;
  const { proxyService } = await import('@/features/media-library/services/proxy-service');
  for (const id of mediaIds) {
    try {
      await proxyService.deleteProxy(id);
      useMediaLibraryStore.getState().clearProxyStatus(id);
    } catch { /* already absent */ }
  }
}

/** Regenerate thumbnails for all media in the current project. */
async function regenerateProjectThumbnails(
  mediaItems: Array<{ id: string; fileName: string; mimeType: string }>,
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  if (mediaItems.length === 0) return 0;
  const [
    { mediaLibraryService },
    { generateThumbnail },
    { saveThumbnail },
    { updateMedia },
  ] = await Promise.all([
    import('@/features/media-library/services/media-library-service'),
    import('@/features/media-library/utils/thumbnail-generator'),
    import('@/lib/storage/indexeddb/thumbnails'),
    import('@/lib/storage/indexeddb/media'),
  ]);

  let regenerated = 0;
  for (const media of mediaItems) {
    try {
      const blob = await mediaLibraryService.getMediaFile(media.id);
      if (!blob) continue;
      const file = new File([blob], media.fileName, { type: media.mimeType });
      const thumbnailBlob = await generateThumbnail(file);
      const thumbnailId = crypto.randomUUID();
      await saveThumbnail({
        id: thumbnailId,
        mediaId: media.id,
        blob: thumbnailBlob,
        timestamp: 1,
        width: 320,
        height: 180,
      });
      await updateMedia(media.id, { thumbnailId });
      mediaLibraryService.clearThumbnailCache(media.id);
      regenerated++;
    } catch (err) {
      log.warn(`Failed to regenerate thumbnail for ${media.fileName}:`, err);
    }
    onProgress?.(regenerated, mediaItems.length);
  }
  await useMediaLibraryStore.getState().loadMediaItems();
  return regenerated;
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const t = useTranslation();
  const showWaveforms = useSettingsStore((s) => s.showWaveforms);
  const showFilmstrips = useSettingsStore((s) => s.showFilmstrips);
  const autoSaveInterval = useSettingsStore((s) => s.autoSaveInterval);
  const maxUndoHistory = useSettingsStore((s) => s.maxUndoHistory);
  const setSetting = useSettingsStore((s) => s.setSetting);
  const resetToDefaults = useSettingsStore((s) => s.resetToDefaults);

  const mediaItems = useMediaLibraryStore((s) => s.mediaItems);

  const [clearState, setClearState] = useState<'idle' | 'clearing' | 'done'>('idle');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [regenState, setRegenState] = useState<'idle' | 'working' | 'done'>('idle');
  const [regenProgress, setRegenProgress] = useState('');
  const [proxyState, setProxyState] = useState<'idle' | 'clearing' | 'done'>('idle');

  const handleClearCache = useCallback(async () => {
    setClearState('clearing');
    try {
      const ids = mediaItems.map((m) => m.id);
      await clearProjectCaches(ids);
      setClearState('done');
      setTimeout(() => setClearState('idle'), 2000);
    } catch (err) {
      log.error('Failed to clear caches', err);
      setClearState('idle');
    }
  }, [mediaItems]);

  const handleRegenThumbnails = useCallback(async () => {
    setRegenState('working');
    setRegenProgress('0/' + mediaItems.length);
    try {
      const items = mediaItems.map((m) => ({ id: m.id, fileName: m.fileName, mimeType: m.mimeType }));
      await regenerateProjectThumbnails(items, (done, total) => {
        setRegenProgress(`${done}/${total}`);
      });
      setRegenState('done');
      setTimeout(() => {
        setRegenState('idle');
        setRegenProgress('');
      }, 2000);
    } catch (err) {
      log.error('Failed to regenerate thumbnails', err);
      setRegenState('idle');
      setRegenProgress('');
    }
  }, [mediaItems]);

  const handleClearProxies = useCallback(async () => {
    setProxyState('clearing');
    try {
      const ids = mediaItems.map((m) => m.id);
      await clearProjectProxies(ids);
      setProxyState('done');
      setTimeout(() => setProxyState('idle'), 2000);
    } catch (err) {
      log.error('Failed to clear proxies', err);
      setProxyState('idle');
    }
  }, [mediaItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>{t.settings.title}</DialogTitle>
          <Button variant="ghost" size="sm" onClick={resetToDefaults} className="h-8 gap-1.5 mr-6">
            <RotateCcw className="w-3.5 h-3.5" />
            {t.settings.reset}
          </Button>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6">
            {/* General */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">{t.settings.general}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t.settings.autoSave}</Label>
                  <Switch
                    checked={autoSaveInterval > 0}
                    onCheckedChange={(v) => setSetting('autoSaveInterval', v ? 5 : 0)}
                  />
                </div>
                {autoSaveInterval > 0 && (
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">{t.settings.interval}</Label>
                    <div className="w-32 flex items-center gap-2">
                      <Slider
                        value={[autoSaveInterval]}
                        onValueChange={([v]) => setSetting('autoSaveInterval', v || 5)}
                        min={5}
                        max={30}
                        step={5}
                      />
                      <span className="text-xs text-muted-foreground w-6">{autoSaveInterval}m</span>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Timeline */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">{t.settings.timeline}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t.settings.showWaveforms}</Label>
                  <Switch checked={showWaveforms} onCheckedChange={(v) => setSetting('showWaveforms', v)} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t.settings.showFilmstrips}</Label>
                  <Switch checked={showFilmstrips} onCheckedChange={(v) => setSetting('showFilmstrips', v)} />
                </div>
              </div>
            </section>

            {/* Performance */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">{t.settings.performance}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t.settings.undoHistoryDepth}</Label>
                  <div className="w-32 flex items-center gap-2">
                    <Slider
                      value={[maxUndoHistory]}
                      onValueChange={([v]) => setSetting('maxUndoHistory', v || 10)}
                      min={10}
                      max={200}
                      step={10}
                    />
                    <span className="text-xs text-muted-foreground w-6">{maxUndoHistory}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Storage */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">{t.settings.storage}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">{t.settings.clearProjectCache}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t.settings.cacheDescription}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-28 gap-1.5"
                    onClick={() => setShowClearConfirm(true)}
                    disabled={clearState !== 'idle'}
                  >
                    {clearState === 'clearing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {clearState === 'done' && <Check className="w-3.5 h-3.5" />}
                    {clearState === 'idle' && <Trash2 className="w-3.5 h-3.5" />}
                    {clearState === 'clearing' ? t.settings.clearing : clearState === 'done' ? t.settings.cleared : t.settings.clear}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">{t.settings.regenerateThumbnails}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t.settings.regenDescription}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-28 gap-1.5"
                    onClick={handleRegenThumbnails}
                    disabled={regenState !== 'idle'}
                  >
                    {regenState === 'working' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {regenState === 'done' && <Check className="w-3.5 h-3.5" />}
                    {regenState === 'idle' && <ImagePlus className="w-3.5 h-3.5" />}
                    {regenState === 'working' ? regenProgress : regenState === 'done' ? t.settings.done : t.settings.regenerate}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">{t.settings.deleteProxies}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t.settings.proxyDescription}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-28 gap-1.5"
                    onClick={handleClearProxies}
                    disabled={proxyState !== 'idle'}
                  >
                    {proxyState === 'clearing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {proxyState === 'done' && <Check className="w-3.5 h-3.5" />}
                    {proxyState === 'idle' && <Film className="w-3.5 h-3.5" />}
                    {proxyState === 'clearing' ? t.settings.deleting : proxyState === 'done' ? t.settings.deleted : t.settings.delete}
                  </Button>
                </div>
              </div>
            </section>

          </div>
        </ScrollArea>
      </DialogContent>

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.settings.confirmClearCache}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.settings.clearCacheDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleClearCache();
              }}
            >
              {t.settings.clear}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
